import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { X, Search } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

/**
 * Pick which of a client's bookings go on an invoice — used both to create a new bundle and
 * to edit an existing (unpaid) one's bookings. A booking already invoiced elsewhere never
 * shows up; one already on THIS invoice (excludeInvoiceId) does, pre-selected.
 */
export function InvoiceBookingPicker({
  clientId, initiallySelected = [], excludeInvoiceId, title, confirmLabel, onConfirm, onClose, pending, error,
}: {
  clientId: string
  initiallySelected?: string[]
  /** When editing an existing invoice, its own id — bookings already on it stay selectable. */
  excludeInvoiceId?: string
  title: string
  confirmLabel: string
  onConfirm: (bookingIds: string[]) => void
  onClose: () => void
  pending?: boolean
  error?: string | null
}) {
  const { formatDate } = useAdminDates()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set(initiallySelected))

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['bookings', clientId],
    queryFn: () => adminApi.getClientBookings(clientId).then(r => r.data),
  })

  const eligible = useMemo(() => {
    const q = search.toLowerCase().trim()
    return bookings
      .filter(b => b.status !== 'Cancelled' && b.status !== 'Rejected')
      .filter(b => !b.invoice || b.invoice.id === excludeInvoiceId || selected.has(b.id))
      .filter(b => !q || b.courseName.toLowerCase().includes(q))
      .sort((a, b) => b.bookingDate.localeCompare(a.bookingDate))
  }, [bookings, search, excludeInvoiceId, selected])

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="px-6 pt-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by course…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-3 space-y-1.5">
          {isLoading && <p className="text-sm text-gray-400 text-center py-6">Loading…</p>}
          {!isLoading && eligible.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No bookings available to invoice.</p>
          )}
          {eligible.map(b => (
            <label
              key={b.id}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                selected.has(b.id) ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <input
                  type="checkbox"
                  checked={selected.has(b.id)}
                  onChange={() => toggle(b.id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{b.courseName}</p>
                  <p className="text-xs text-gray-500">
                    {formatDate(b.bookingDate, { day: 'numeric', month: 'short', year: 'numeric' })} · {b.places} place{b.places === 1 ? '' : 's'}
                    {b.invoice && b.invoice.id === excludeInvoiceId && ' · already on this invoice'}
                  </p>
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="px-6 py-4 border-t space-y-3 shrink-0">
          <p className="text-sm text-gray-500">
            {selected.size} booking{selected.size === 1 ? '' : 's'} selected
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={selected.size === 0 || pending} onClick={() => onConfirm([...selected])}>
              {pending ? 'Saving…' : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
